import { Fuzzy } from '@utils/fuse-search'
import { Field, Selected } from './types'

/**
 * Performs fuzzy search on list of fields and return the results
 *
 * @param {Field[]} collection Array of objects to search on
 * @param {string} term The search term
 * @returns {Field[]} Search results
 */
export function fuzzySearch(collection: Field[], term: string): Field[] {
  const fuzzy = new Fuzzy(collection, { keys: ['name'] }, 0.3)
  const results = fuzzy.search(term)

  return results
}

/**
 * Used to update the selected fields when the component mounts.
 * The default selected fields will either be:
 *
 * 1) Field ids from the URL
 * 2) The fields manually selected by the user
 * 3) All visible fields on the dataset
 *
 * @param {Field[]} allColumns All columns on the dataset
 * @param {Field[] | null} filteredFields Fields manually selected by the user
 * @returns {Selected} An object used to determine which fieldIds are selected
 */
export function getDefaultSelected(allColumns: Field[], filteredFields: string[] | null): Selected {
  const defaultSelected = (() => {
    if (filteredFields?.length) {
      return getSelectedFromFiltered(allColumns, filteredFields)
    }

    const selected = allColumns.reduce((acc, col) => ({ ...acc, [col.fieldId]: col.visible }), {})

    return selected
  })()

  return defaultSelected
}

function getSelectedFromFiltered(allColumns: Field[], filtered: string[]): Selected {
  const newSelected = {}

  allColumns.forEach(field => {
    const { fieldId } = field
    const rowPropertyName = field?.rowPropertyName
    const key = rowPropertyName || String(fieldId)

    if (filtered.includes(key)) {
      newSelected[key] = true
    } else {
      newSelected[key] = false
    }
  })

  return newSelected
}

/**
 * Checked if all fields are selected
 *
 * @param {string[]} fieldIds The list of fields visible to user
 * @param {Selected} initialSelected The selected object before any changes
 * @returns True if all fields are selected, false otherwise
 */
export function getAllSelected(fieldIds: string[], initialSelected: Selected) {
  let allSelected = true

  if (fieldIds.length === 0) {
    allSelected = false
  }

  fieldIds.forEach(fieldId => {
    if (!initialSelected[fieldId]) {
      allSelected = false
    }
  })

  return allSelected
}

export function getFilteredFieldIds(
  filteredFields: Field[],
  searchResults: Field[],
  fieldsExpanded: boolean,
  propertiesExpanded: boolean,
): string[] {
  let fields = filteredFields.map(getFieldParam)

  if (searchResults.length > 0) {
    fields = searchResults.map(getFieldParam)
  }

  if (fieldsExpanded && !propertiesExpanded) {
    fields = fields.filter(id => !Number.isNaN(+id))
  }

  if (!fieldsExpanded && propertiesExpanded) {
    fields = fields.filter(id => Number.isNaN(+id))
  }

  return fields
}

export function getFieldParam(field: Field): string {
  return field?.rowPropertyName || getFieldIdAndConvertToString(field)
}

function getFieldIdAndConvertToString(field: Field): string {
  return String(field.fieldId)
}

export function isSelected(field: Field, selected: Selected): boolean {
  return selected[field?.rowPropertyName || field.fieldId] ?? false
}

export function updateVisibleToTrue(field: Field): Field {
  return {
    ...field,
    visible: true,
  }
}

export function addDefaultHidden(field: Field): Field {
  return {
    ...field,
    defaultHidden: !field.rowPropertyName && !field.visible,
  }
}

export function getSelectedInCurrentList({
  fields,
  searchResults,
  selected,
}: {
  fields: Field[]
  searchResults: Field[]
  selected: Selected
}): Selected {
  return Object.keys(selected)
    .filter(key =>
      (searchResults.length > 0 ? searchResults : fields).find(field => {
        const toCompare = String(field?.rowPropertyName || field.fieldId)
        return toCompare == key
      }),
    )
    .reduce(
      (acc, key) => ({
        ...acc,
        [key]: selected[key],
      }),
      {},
    )
}

export function getColCounts(all: Field[], filtered: Field[] | null) {
  const defaultHiddenCount = (filtered || all).filter(
    col => col.defaultHidden && !col?.rowPropertyName,
  ).length
  const rowPropertiesCount = filtered ? filtered.filter(col => col?.rowPropertyName).length : 0

  return {
    defaultHiddenCount,
    rowPropertiesCount,
  }
}
// end Pendo event tracking logic
